import { z } from "zod";

import { DbEnum } from "../../generated/prisma-enums";
import { ALL_EFFORT_LEVELS } from "../../models/model-catalog.config";
import { STORED_NONE } from "../../models/model-controls";
import { ANTHROPIC_THINKING_DISPLAY_VALUES } from "../../models/model-parser";
import { RetiredEffortLevelFieldsShape } from "./retired-effort-level-fields";

function withStoredOnlyWords<
  const Vocabulary extends readonly [string, ...string[]],
  const StoredOnly extends string
>(
  vocabulary: Vocabulary,
  storedOnly: readonly StoredOnly[]
): readonly [Vocabulary[number], ...(Vocabulary[number] | StoredOnly)[]] {
  const [first, ...rest] = vocabulary;
  const isNew = (word: StoredOnly) => !(vocabulary as readonly string[]).includes(word);
  return [first, ...rest, ...storedOnly.filter(isNew)];
}

export const STORED_EFFORT_LEVELS = withStoredOnlyWords(ALL_EFFORT_LEVELS, [STORED_NONE]);

/**
 * ModelConfigBaseShape — raw Zod shape for model selection fields.
 *
 * Shared across all model-config schemas (internal domain, API request bodies,
 * public v1). Consumers call:
 *   z.object(ModelConfigBaseShape)
 *   z.object({ ...ModelConfigBaseShape, modelName: z.string().min(1) })
 *
 * customModelId is intentionally absent: BYOM is an internal feature
 * gated to the canonical ModelConfigSchema and ModelConfigWriteSchema only.
 */
export const ModelConfigBaseShape = {
  modelName: z
    .string()
    .max(255)
    .describe("Model ID from the models catalog (e.g. 'claude-sonnet-4-6', 'gpt-4.1')"),
  modelProvider: z
    .nativeEnum(DbEnum.ModelProvider)
    .describe(`Provider: ${DbEnum.MODEL_PROVIDER_VALUES.join(", ")}`),
  /**
   * How hard the model thinks for a turn — ONE field for every provider.
   *
   * 🔴 **ONE FIELD, NOT ONE ENUM. THE DIALECT DECIDES THE ALPHABET — BECAUSE THE
   * VOCABULARIES OVERLAP, NOT BECAUSE THEY ARE DISJOINT.** The accepted set here is
   * the union of every dialect's vocabulary, because a stored value is read back
   * for whatever model the agent points at TODAY. Most dialect pairs share at least
   * one value: `openai-reasoning` is `anthropic-adaptive`'s five plus `minimal`, and
   * `kimi-effort`'s three are a strict SUBSET of both. So a
   * value never says which dialect chose it — the model's dialect, read at send time,
   * is the only thing that can.
   *
   * What each vocabulary does own is a distinguishing edge, and those are the facts
   * the narrowing rests on: `fast`/`detailed`/`extended` are `anthropic-legacy`'s
   * alone, `dynamic` is `gemini-budget`'s, `minimal` is shared by `gemini-level` and
   * `openai-reasoning`, and Kimi
   * accepts neither `medium` nor `xhigh`. `thinking-dialect.registry.test.ts`'s
   * "vocabulary relations" block pins every clause of this paragraph, so a dialect
   * that grows or loses a value reds there rather than leaving this prose wrong.
   *
   * The per-model narrowing is therefore a runtime decision, never something this
   * enum can express. A WRITE is judged by `refuseAChangedEffortLevel` in each use
   * case, against the stored config, so only a level the write changes has to be
   * one the model offers. A contract refine cannot make that call, because it never
   * sees the stored side: it refuses a carried word exactly like a new one. At send
   * time the resolver maps a stored level the model no longer offers onto one it
   * does.
   *
   * `none` is admitted as a stored-only word: it is offered only where the model
   * has an off form, and the use-case policy decides that.
   *
   * The spelling is the one the platform already uses for this concept: the
   * Cue surface stores a single `reasoningLevel` and hands it to
   * `buildReasoningConfig`, the same registry entry point every dialect reaches.
   *
   * It is the ONLY level field. The four provider-prefixed names it replaced are
   * refused on input ({@link RetiredEffortLevelFieldsShape}, spread below) and
   * read off no stored row.
   */
  reasoningLevel: z
    .enum(STORED_EFFORT_LEVELS)
    .optional()
    .describe(
      "Reasoning/thinking level. Valid values depend on the model's dialect — see supportedReasoningLevels on the model"
    ),
  thinkingDisplay: z
    .enum(ANTHROPIC_THINKING_DISPLAY_VALUES)
    .optional()
    .describe("Anthropic adaptive thinking display mode"),
  temperature: z.number().min(0).max(1).optional().describe("Sampling temperature (0–1)"),
  ...RetiredEffortLevelFieldsShape
};

/**
 * The tuning fields of a STORED config, each read leniently: a value a field does
 * not accept reads as absent rather than failing the row. The retired level names
 * are not here — no stored row carries one, and a read strips any key it does not
 * name.
 */
const StoredTuningReadShape = {
  reasoningLevel: ModelConfigBaseShape.reasoningLevel.catch(undefined),
  thinkingDisplay: ModelConfigBaseShape.thinkingDisplay.catch(undefined),
  temperature: ModelConfigBaseShape.temperature.catch(undefined)
};

/**
 * ModelConfigSchema — unified model selection stored in JSON fields.
 *
 * Used by Agent.modelConfig, AITask.modelConfig, AgentTask.modelConfig.
 * Captures the user's full model selection: which model, provider, and its
 * tuning (reasoning level, thinking display, temperature). A READ schema: it
 * never refuses a row over one bad field, and never parses a request body —
 * a write takes {@link ModelConfigWriteSchema}, which refuses the retired names.
 */
export const ModelConfigSchema = z.object({
  modelName: ModelConfigBaseShape.modelName,
  modelProvider: ModelConfigBaseShape.modelProvider,
  ...StoredTuningReadShape,
  customModelId: z.string().optional() // BYOM: references CustomModel.id — internal only
});

export type ModelConfig = z.infer<typeof ModelConfigSchema>;

export const ModelConfigWriteSchema = z.object({
  ...ModelConfigBaseShape,
  customModelId: z.string().optional()
});
